import React from 'react';
import Autosuggest from 'react-autosuggest';
import {useHistory, Link} from 'react-router-dom';
import {Alert, Button} from 'reactstrap';

import withAdvancedSearchInput from 'modules/advanced-search/withAdvancedSearchInput';
import useGetSearchInputLimit from 'modules/advanced-search/useGetSearchInputLimit';
import {useMembership} from 'contexts/MembershipContext';
import {SUBSCRIPTION_TYPE_LEVEL} from 'configs/membership';
import {ImageFlags} from 'components/Images';
import {getCountryCodeById} from 'utils/locales';
import {getFormattedNumber} from 'utils/numbers';
import cx from 'classnames';
import styles from './AdvancedSearchInput.module.scss';
import {isEmpty} from 'lodash';
import {UNABLE_TO_FIND_IMAGE_MATCHED} from 'configs';
import {useViewport} from 'contexts/ViewportContext';
import {VIEWPORT_WIDTHS} from 'configs';
import {MDB_PREFIX} from 'configs';
import urlJoin from 'url-join';
import {removePrefix} from 'utils/string';
import {alertConfirm} from 'utils/alerts';
import {SUBSCRIPTION_BILLING_METHOD} from 'configs/membership';
import MuiIcon from 'components/Icons/MuiIcon';

const SEARCH_TYPE = {
  cert: 'cert',
  ce: 'ce',
};

const limitReachedOption = {
  type: 'warning',
  title: 'To view more company certificates upgrade your account',
  html: `<div></div>`,
  footer: `<div class="d-flex">
  <a class="btn btn-outline-primary rounded mr-2" href="/advanced-search" onclick="event.preventDefault();">Cancel</a>
  <a class="btn btn-primary rounded" href="/membership/plans">Upgrade Account</a>
  </div>`,
  showConfirmButton: false,
  showCloseButton: true,
  showCancelButton: false,
};

function AdvancedSearchInputV1(props) {
  const {stateProps, suggestionProps, header, ...rest} = props;
  const history = useHistory();

  const {size} = useViewport();
  const isMobile = VIEWPORT_WIDTHS[size] < VIEWPORT_WIDTHS['sm'];
  const {subscriptionLevel, billingMethod} = useMembership();

  const {limit, getLimit, isLoading: isLimitLoading} = useGetSearchInputLimit();

  const {
    searchType,
    keyword,
    suggestions,
    isLoading,
    hasSearched,
    isFocused,
    setKeyword,
    setHasSearched,
    setIsFocused,
    // getSuggestion,
    clearSuggestions,
    clearAllStates,
    recordsCount,
  } = stateProps;

  const {
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    // handleSuggestionSelected,
    getSuggestionValue,
    themeGenerator,
    renderInputGenerator,
    inputProps,
  } = suggestionProps;

  const isCertSearchType = searchType === 'cert';
  const limitData = limit[SEARCH_TYPE[searchType]];
  const {try: limitTry, max: limitMax} = limitData || {};
  const limitReached = limitTry === limitMax && !isLimitLoading;

  const isEnterprise =
    subscriptionLevel === SUBSCRIPTION_TYPE_LEVEL['enterprise'];
  const isManualMethod = billingMethod === SUBSCRIPTION_BILLING_METHOD.manual;

  const overallFocused = isFocused && keyword;
  const overallLoading = isLimitLoading || isLoading;

  const overallLimitReached =
    limitReached && (!isEnterprise || (isEnterprise && isManualMethod));
  const displayLimit = !isEnterprise || (isEnterprise && isManualMethod);

  function renderSuggestion(suggestion, isLimitReached) {
    const {company_name, countries_id} = suggestion;

    return (
      <div
        className={cx(
          isLimitReached && styles['single-search__suggestion-limit-reached']
        )}
      >
        {countries_id && (
          <>
            <ImageFlags
              countryCode={getCountryCodeById(countries_id)}
              className="d-inline-block mr-2"
            />
            <span>{company_name}</span>
          </>
        )}
        {!countries_id && (
          <div className="d-flex">
            <div className={styles['confidential-icon']}>
              <MuiIcon name="lock" style={{margin: 0, padding: 0}} />
            </div>
            <span>{company_name}</span>
          </div>
        )}
      </div>
    );
  }

  function renderSuggestionsContainer({containerProps, children}) {
    const {className, ...rest} = containerProps;

    const creditLimitText = isCertSearchType
      ? 'certificate verification'
      : 'company verification';
    const creditText = isCertSearchType
      ? 'certificate verification'
      : 'company verification';

    let limitText = limitReached ? (
      isEnterprise && isManualMethod ? (
        <span>
          You have used up all your available credits. To view more contact us{' '}
          <Link to="/contact">here</Link>.
        </span>
      ) : (
        <span>
          You have used up all your {creditLimitText} credits. To view more
          upgrade <Link to="/membership/plans">here</Link>.
        </span>
      )
    ) : (
      <span>
        You have used{' '}
        <strong>
          {getFormattedNumber(limitTry)}/{getFormattedNumber(limitMax)}
        </strong>{' '}
        of your {creditText} credits. Learn more{' '}
        <Link to="/membership/plans">here</Link>.
      </span>
    );
    const alertColor = limitReached ? 'danger' : 'primary';

    const limitAlert = (
      <div className="m-3">
        <Alert fade={false} color={alertColor}>
          <MuiIcon name={limitReached ? 'error' : 'info'} className="mr-2" />
          {limitText}
        </Alert>
      </div>
    );

    if (overallLoading) {
      return (
        <div
          className={cx(
            className,
            styles['single-search__suggestions-container--loading']
          )}
          {...rest}
        >
          <div className="d-flex align-items-center justify-content-center h-100">
            <p className="m-auto">Loading...</p>
          </div>
        </div>
      );
    }

    if (!hasSearched) {
      return (
        <div className={className} {...rest}>
          {/* {displayLimit && limitAlert} */}
        </div>
      );
    }

    if (hasSearched && isEmpty(suggestions)) {
      return (
        <div className={className} {...rest}>
          <div
            className={cx(
              styles['single-search__suggestions-container--no-results'],
              'm-3 p-4'
            )}
          >
            <img
              src={UNABLE_TO_FIND_IMAGE_MATCHED}
              alt="try"
              className="mr-3"
            />
            <div>
              <h4 className="mb-0">Unable to find matched results</h4>
              <p className="mb-0">Can't find the company you're looking for?</p>

              <p className="mb-0">
                <Link to="/enquiry" className="text-primary font-weight-bold">
                  Send a request here
                </Link>
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (hasSearched && !isEmpty(suggestions)) {
      return (
        <div className={className} {...rest}>
          {children}
          {(!limitReached || (isEnterprise && !isManualMethod)) && (
            <Button
              block
              outline
              color="white"
              className={cx(
                styles['single-search__suggestions-container-view-more'],
                'p-2 text-primary d-flex justify-content-start'
              )}
              type="submit"
            >
              <span className="mx-2">
                {recordsCount} record(s) found, view more results here
              </span>
            </Button>
          )}
          {limitReached && displayLimit && <hr />}
          {limitReached && displayLimit && limitAlert}

          <hr className="mt-3 mb-0" />
          {/* {limitReached && isFree && (
            <Button
              tag={Link}
              to="/membership/plans"
              block
              outline
              color="white"
              className={cx(
                styles['single-search__suggestions-container-view-more'],
                'p-2 mb-0 text-danger'
              )}
            >
              <strong>Upgrade</strong> to search for more companies
            </Button>
          )} */}
          {/* {limitReached && isEnterprise && isManualMethod && (
            <Button
              tag={Link}
              to="/contact"
              block
              outline
              color="white"
              className={cx(
                styles['single-search__suggestions-container-view-more'],
                'p-2 mb-0'
              )}
            >
              <strong>Contact Us</strong> to search for more companies
            </Button>
          )} */}
          {/* If limit is not reached, show view more button */}
        </div>
      );
    }
  }

  const theme = themeGenerator(overallFocused, isMobile, overallLimitReached);

  const renderInputComponent = (inputProps) =>
    renderInputGenerator(
      inputProps,
      overallFocused,
      isMobile,
      header && {
        outline: true,
        color: 'white',
        children: <MuiIcon name="search" className="text-primary" size="md" />,
        className: 'p-1',
      }
    );

  const _inputProps = {
    ...inputProps,
    onChange: (event, {newValue}) => {
      if (
        overallLimitReached &&
        (event.key === 'ArrowUp' || event.key === 'ArrowDown')
      ) {
        event.preventDefault();

        return;
      }

      setKeyword(newValue || '');
      if (!newValue) {
        clearSuggestions();
        setHasSearched(false);
      }
    },
    onFocus: async () => {
      setIsFocused(true);
      if (keyword) setHasSearched(true);

      await getLimit();
    },
  };

  async function onSuggestionSelected(event, {suggestion}) {
    event.preventDefault();

    const isCertSuggestion = !!suggestion.certification_number;

    setHasSearched(false);
    setIsFocused(false);
    clearSuggestions();

    if (isCertSuggestion) {
      const {certification_number} = suggestion;

      if (overallLimitReached) {
        document.activeElement.blur();
        await alertConfirm(limitReachedOption);
        clearAllStates();
      } else {
        history.push({
          pathname: urlJoin(
            '/certification',
            removePrefix(certification_number, MDB_PREFIX.cert)
          ),
          state: {
            keyword: keyword,
          },
        });
      }
    } else {
      const {company_id, company_name, countries_id} = suggestion;

      if (overallLimitReached) {
        document.activeElement.blur();
        await alertConfirm(limitReachedOption);
        clearAllStates();
      } else {
        history.push({
          pathname: urlJoin(
            '/certified-entity',
            removePrefix(company_id, MDB_PREFIX.ce)
          ),
          state: {
            keyword: keyword,
            company_name: company_name,
            countries_id: countries_id,
          },
        });
      }
    }
  }

  return (
    <Autosuggest
      theme={theme}
      alwaysRenderSuggestions
      suggestions={suggestions}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={onSuggestionSelected}
      getSuggestionValue={getSuggestionValue}
      inputProps={_inputProps}
      renderSuggestion={(suggestion) => {
        return renderSuggestion(suggestion, overallLimitReached);
      }}
      renderSuggestionsContainer={renderSuggestionsContainer}
      renderInputComponent={renderInputComponent}
      {...rest}
    />
  );
}

export default withAdvancedSearchInput(AdvancedSearchInputV1);
